import React, { useState, useEffect, useCallback } from 'react';
import { api } from './api';
import { Icon } from './icons';

type Entry = {
  id: string; title: string; body: string; tags: string[];
  status: string; source_channel_id: string | null; created_at: string; sim?: number;
};

/** Knowledge Base — browse/search published entries + review AI-drafted ones. Entries are
 *  auto-detected from chat by the backend (kb.scan_channel) and embedded for semantic search. */
export function KnowledgeView({ workspaceId, channels }: {
  workspaceId?: string | null;
  channels: { id: string; name?: string; kind?: string }[];
}) {
  const [tab, setTab] = useState<'published' | 'draft'>('published');
  const [entries, setEntries] = useState<Entry[]>([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const query = q.trim();
      const r = query ? await api.kbList(workspaceId, { q: query }) : await api.kbList(workspaceId, { status: tab });
      setEntries(r.entries || []);
    } catch { setEntries([]); }
    finally { setLoading(false); }
  }, [workspaceId, tab, q]);

  useEffect(() => { const t = setTimeout(load, q ? 350 : 0); return () => clearTimeout(t); }, [load, q]);

  const scan = async () => {
    if (!workspaceId) return;
    setScanning(true); setNote('');
    let drafts = 0;
    const pub = channels.filter((c) => (c.kind || 'public') === 'public').slice(0, 12);
    for (const c of pub) {
      try { const r = await api.kbScan(c.id); drafts += r.drafts || 0; } catch { /* skip channel */ }
    }
    setNote(`Scanned ${pub.length} channel${pub.length === 1 ? '' : 's'} — ${drafts} new draft${drafts === 1 ? '' : 's'}.`);
    setTab('draft'); setQ('');
    setScanning(false);
    load();
  };

  const publish = async (id: string) => { if (!workspaceId) return; try { await api.kbPublish(workspaceId, id); load(); } catch { /* noop */ } };
  const dismiss = async (id: string) => {
    if (!workspaceId || !window.confirm('Discard this draft entry?')) return;
    try { await api.kbDelete(workspaceId, id); load(); } catch { /* noop */ }
  };
  const chName = (cid: string | null) => channels.find((c) => c.id === cid)?.name;

  return (
    <main className="fx-chat fx-kb">
      <header className="fx-kb-head">
        <div className="fx-kb-title"><Icon name="skills" size={18} /> Knowledge Base</div>
        <button type="button" className="fx-kb-scan" onClick={scan} disabled={scanning}>
          <Icon name="bolt" size={13} /> {scanning ? 'Scanning…' : 'Scan channels for knowledge'}
        </button>
      </header>

      <div className="fx-kb-bar">
        <div className="fx-kb-search">
          <Icon name="search" size={14} />
          <input placeholder="Search the knowledge base…" value={q} onChange={(e) => setQ(e.target.value)} />
          {q && <button type="button" onClick={() => setQ('')} title="Clear">×</button>}
        </div>
        {!q && (
          <div className="fx-kb-tabs">
            <button type="button" className={tab === 'published' ? 'is-on' : ''} onClick={() => setTab('published')}>Published</button>
            <button type="button" className={tab === 'draft' ? 'is-on' : ''} onClick={() => setTab('draft')}>Drafts</button>
          </div>
        )}
      </div>

      {note && <div className="fx-kb-note">{note}</div>}

      <div className="fx-kb-list">
        {loading ? (
          <div className="fx-kb-empty">Loading…</div>
        ) : entries.length === 0 ? (
          <div className="fx-kb-empty">
            {q ? 'No matches in the knowledge base.'
              : tab === 'published' ? 'Nothing published yet — scan your channels, then publish the good drafts.'
              : 'No drafts yet — hit “Scan channels for knowledge” to mine your conversations.'}
          </div>
        ) : entries.map((e) => (
          <div className="fx-kb-card" key={e.id}>
            <div className="fx-kb-card-h">
              <span className="fx-kb-card-title">{e.title}</span>
              {e.status === 'draft' && <span className="fx-kb-badge">Draft</span>}
              {typeof e.sim === 'number' && <span className="fx-kb-sim">{Math.round(e.sim * 100)}% match</span>}
            </div>
            <div className="fx-kb-card-body">{e.body}</div>
            <div className="fx-kb-card-foot">
              {(e.tags || []).map((t) => <span className="fx-kb-tag" key={t}>#{t}</span>)}
              {chName(e.source_channel_id) && <span className="fx-kb-src">from #{chName(e.source_channel_id)}</span>}
              {e.status === 'draft' && (
                <span className="fx-kb-actions">
                  <button type="button" className="fx-kb-pub" onClick={() => publish(e.id)}>Publish</button>
                  <button type="button" className="fx-kb-dis" onClick={() => dismiss(e.id)}>Dismiss</button>
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </main>
  );
}
